// Editing commands used by menus, toolbar, context menus and shortcuts. Each builds operations and submits them.
import { colToIndex, indexToCol, moveFormula, shiftFormula, translateFormula } from '../../engine/src/index.ts';
import type { StructuralChange } from '../../engine/src/index.ts';
import type { Format, Op } from '../../shared/ops.ts';
import type { Range } from './grid.ts';
import type { Grid } from './grid.ts';
import type { DocModel } from './model.ts';
import { formatRangeOp } from './formatops.ts';
import { dialog, h, toast } from './ui.ts';

export interface Ctx { model: DocModel; grid: Grid; sheet(): number; submit(op: Op): void }
export const ALL_FMT_KEYS = ['b', 'i', 's', 'u', 'fs', 'ff', 'color', 'bg', 'pattern', 'patternColor', 'ha', 'va', 'wrap', 'indent', 'nf', 'rot', 'bt', 'bb', 'bl', 'br', 'note', 'link', 'person', 'rich'];

export const props = (x: Ctx): Record<string, any> => x.model.sheet(x.sheet())?.props ?? {};
export const setProps = (x: Ctx, patch: Record<string, unknown>): void => x.submit({ t: 'props', sheet: x.sheet(), patch });
export function fmtRange(x: Ctx, R: Range, fmt: Format): void {
  x.submit(formatRangeOp(x.model, x.sheet(), R, fmt));
}
/** A range as the name box shows it: B3, B3:C4, whole columns B:D, whole rows 2:5 (when the sheet size is given). */
export const refText = (R: Range, rows?: number, cols?: number): string => {
  const allRows = rows !== undefined && R.r1 === 0 && R.r2 >= rows - 1, allCols = cols !== undefined && R.c1 === 0 && R.c2 >= cols - 1;
  if (allRows && !allCols) return `${indexToCol(R.c1)}:${indexToCol(R.c2)}`;
  if (allCols) return `${R.r1 + 1}:${R.r2 + 1}`;
  return R.r1 === R.r2 && R.c1 === R.c2 ? `${indexToCol(R.c1)}${R.r1 + 1}` : `${indexToCol(R.c1)}${R.r1 + 1}:${indexToCol(R.c2)}${R.r2 + 1}`;
};
/**
 * Parse what was typed into the name box (or a #range= link): A1, $B$2, b3:c4, C3:A1, whole columns B:B / B:D,
 * whole rows 2:2 / 2:5, open-ended A2:B, optionally after a sheet name ('My sheet'!A1).
 */
export function parseRefText(text: string, rows: number, cols: number): { sheet: string | null; R: Range } | null {
  let t = text.trim(), sheet: string | null = null;
  const m = /^(?:'((?:[^']|'')+)'|([^!']+))!(.+)$/.exec(t);
  if (m) { sheet = (m[1] ?? m[2]).replace(/''/g, "'"); t = m[3]; }
  const parts = t.replace(/\$/g, '').toUpperCase().split(':');
  if (parts.length > 2 || parts.some(p => !p)) return null;
  const [a, b = a] = parts;
  const cell = (x: string): { r: number | null; c: number | null } | null => {
    const k = /^([A-Z]{1,3})?(\d{1,7})?$/.exec(x);
    if (!k || (!k[1] && !k[2])) return null;
    return { c: k[1] ? colToIndex(k[1]) : null, r: k[2] ? Number(k[2]) - 1 : null };
  };
  const p = cell(a), q = cell(b);
  if (!p || !q || (p.r !== null && p.r < 0) || (q.r !== null && q.r < 0)) return null;
  // a lone column or row needs a partner of the same kind (B:D, 2:5); a lone "B" or "2" is not a reference
  if (parts.length === 1 && (p.r === null || p.c === null)) return null;
  if ((p.c === null) !== (q.c === null) && p.r === null) return null;
  const r1 = p.r ?? 0, r2 = q.r ?? rows - 1, c1 = p.c ?? 0, c2 = q.c ?? cols - 1;
  return { sheet, R: { r1: Math.min(r1, r2), c1: Math.min(c1, c2), r2: Math.max(r1, r2), c2: Math.max(c1, c2) } };
}

// ----- borders -----
export type BorderMode = 'all' | 'inner' | 'horizontal' | 'vertical' | 'outer' | 'left' | 'top' | 'right' | 'bottom' | 'clear';
export function borders(x: Ctx, mode: BorderMode, spec: string): void {
  const R = x.grid.range, s = x.sheet(), ops: Op[] = [], m = x.model;
  const rows = m.sheet(s)?.rows ?? R.r2 + 2, cols = m.sheet(s)?.cols ?? R.c2 + 2;
  const f = (r1: number, c1: number, r2: number, c2: number, fmt: Format): void => {
    r1 = Math.max(0, r1); c1 = Math.max(0, c1); r2 = Math.min(rows - 1, r2); c2 = Math.min(cols - 1, c2);
    if (r1 <= r2 && c1 <= c2) ops.push({ t: 'fmt', sheet: s, r1, c1, r2, c2, fmt });
  };
  const v = mode === 'clear' ? null : spec;
  // A line between two cells is stored on both and the grid draws the wider one (imported files can disagree), so an
  // explicit edge choice drops a different value the outside neighbour still holds; only cells that have one change.
  const across = (side: 'bt' | 'bb' | 'bl' | 'br'): void => {
    const [opp, r1, c1, r2, c2] = side === 'bt' ? ['bb', R.r1 - 1, R.c1, R.r1 - 1, R.c2] as const : side === 'bb' ? ['bt', R.r2 + 1, R.c1, R.r2 + 1, R.c2] as const
      : side === 'bl' ? ['br', R.r1, R.c1 - 1, R.r2, R.c1 - 1] as const : ['bl', R.r1, R.c2 + 1, R.r2, R.c2 + 1] as const;
    if (r1 < 0 || c1 < 0 || r1 >= rows || c1 >= cols) return;
    const stale = (r: number, c: number, fmt: Format | undefined): void => {
      const old = fmt?.[opp];
      if (typeof old === 'string' && old !== v) f(r, c, r, c, { [opp]: null });
    };
    // a whole row/column edge visits the sparse format map instead of every (mostly empty) neighbour
    const r2c = Math.min(r2, rows - 1), c2c = Math.min(c2, cols - 1);
    if ((r2c - r1 + 1) * (c2c - c1 + 1) <= m.fmts.size) { for (let r = r1; r <= r2c; r++) for (let c = c1; c <= c2c; c++) stale(r, c, m.fmt(s, r, c)); }
    else for (const [key, fmt] of m.fmts) {
      const [sid, r, c] = key.split(':').map(Number);
      if (sid === s && r >= r1 && r <= r2c && c >= c1 && c <= c2c) stale(r, c, fmt);
    }
  };
  // as in Sheets, a line between two cells of the range belongs to both (the bottom of one and the top of the next)
  const innerH = (): void => { f(R.r1, R.c1, R.r2 - 1, R.c2, { bb: v }); f(R.r1 + 1, R.c1, R.r2, R.c2, { bt: v }); };
  const innerV = (): void => { f(R.r1, R.c1, R.r2, R.c2 - 1, { br: v }); f(R.r1, R.c1 + 1, R.r2, R.c2, { bl: v }); };
  switch (mode) {
    case 'all': f(R.r1, R.c1, R.r2, R.c2, { bt: v, bb: v, bl: v, br: v }); for (const side of ['bt', 'bb', 'bl', 'br'] as const) across(side); break;
    case 'clear': {
      // the neighbours' sides that touch the range go too, or their lines would still show around it
      f(R.r1, R.c1, R.r2, R.c2, { bt: null, bb: null, bl: null, br: null });
      if (R.r1 > 0) f(R.r1 - 1, R.c1, R.r1 - 1, R.c2, { bb: null });
      f(R.r2 + 1, R.c1, R.r2 + 1, R.c2, { bt: null });
      if (R.c1 > 0) f(R.r1, R.c1 - 1, R.r2, R.c1 - 1, { br: null });
      f(R.r1, R.c2 + 1, R.r2, R.c2 + 1, { bl: null });
      break;
    }
    case 'outer':
      f(R.r1, R.c1, R.r1, R.c2, { bt: v }); f(R.r2, R.c1, R.r2, R.c2, { bb: v }); f(R.r1, R.c1, R.r2, R.c1, { bl: v }); f(R.r1, R.c2, R.r2, R.c2, { br: v });
      for (const side of ['bt', 'bb', 'bl', 'br'] as const) across(side);
      break;
    case 'inner': innerH(); innerV(); break;
    case 'horizontal': innerH(); break;
    case 'vertical': innerV(); break;
    case 'left': f(R.r1, R.c1, R.r2, R.c1, { bl: v }); across('bl'); break;
    case 'right': f(R.r1, R.c2, R.r2, R.c2, { br: v }); across('br'); break;
    case 'top': f(R.r1, R.c1, R.r1, R.c2, { bt: v }); across('bt'); break;
    case 'bottom': f(R.r2, R.c1, R.r2, R.c2, { bb: v }); across('bb'); break;
  }
  if (ops.length) x.submit({ t: 'batch', ops });
}

// ----- merging -----
export async function merge(x: Ctx, mode: 'all' | 'horizontal' | 'vertical' | 'none'): Promise<void> {
  const R = x.grid.range, p = props(x);
  const fr = Number(p.frozenRows ?? 0), fc = Number(p.frozenCols ?? 0);
  if (mode !== 'none' && ((fr && R.r1 < fr && R.r2 >= fr) || (fc && R.c1 < fc && R.c2 >= fc))) { toast('고정된 영역과 고정되지 않은 영역에 걸친 셀은 병합할 수 없습니다'); return; }
  const others = ((p.merges ?? []) as Range[]).filter(m => m.r2 < R.r1 || m.r1 > R.r2 || m.c2 < R.c1 || m.c1 > R.c2);
  const add: Range[] = [];
  if (mode === 'all') add.push(R);
  else if (mode === 'horizontal') for (let r = R.r1; r <= R.r2; r++) add.push({ r1: r, c1: R.c1, r2: r, c2: R.c2 });
  else if (mode === 'vertical') for (let c = R.c1; c <= R.c2; c++) add.push({ r1: R.r1, c1: c, r2: R.r2, c2: c });
  const merges = [...others, ...add.filter(m => m.r1 !== m.r2 || m.c1 !== m.c2)];
  // like Sheets, only the top-left value of each merged area is kept (after asking)
  const ops: Op[] = [{ t: 'props', sheet: x.sheet(), patch: { merges: merges.length ? merges : null } }];
  for (const m of add) for (let r = m.r1; r <= m.r2; r++) for (let c = m.c1; c <= m.c2; c++) if ((r !== m.r1 || c !== m.c1) && x.model.raw(x.sheet(), r, c) !== '') ops.push({ t: 'set', sheet: x.sheet(), r, c, raw: null });
  if (ops.length > 1 && !await dialog('셀 병합', h('p', {}, '셀을 병합하면 가장 왼쪽 위의 값만 유지됩니다.'), [{ label: '취소', value: false }, { label: '확인', value: true, primary: true }])) return;
  x.submit({ t: 'batch', ops });
}

// ----- rows / columns -----
export function hide(x: Ctx, axis: 'row' | 'col', hidden: boolean): void {
  const R = x.grid.range, key = axis === 'row' ? 'hiddenRows' : 'hiddenCols';
  const set = new Set<number>((props(x)[key] ?? []) as number[]);
  const [a, b] = axis === 'row' ? [R.r1, R.r2] : [R.c1, R.c2];
  for (let i = a; i <= b; i++) hidden ? set.add(i) : set.delete(i);
  // unhiding next to a hidden block (selection around it) also reveals the block
  if (!hidden) { for (let i = a - 1; set.has(i); i--) set.delete(i); for (let i = b + 1; set.has(i); i++) set.delete(i); }
  setProps(x, { [key]: [...set].sort((p, q) => p - q) });
}
export function freeze(x: Ctx, axis: 'row' | 'col', count: number): void {
  const split = ((props(x).merges ?? []) as Range[]).some(m => (axis === 'row' ? m.r1 < count && m.r2 >= count : m.c1 < count && m.c2 >= count));
  if (count && split) { toast('병합된 셀을 일부만 고정할 수는 없습니다'); return; }
  setProps(x, { [axis === 'row' ? 'frozenRows' : 'frozenCols']: count || null });
}

// ----- groups (View > Group) -----
type Span = { start: number; end: number; collapsed?: boolean };
/** Group the selected rows/columns, or ungroup them (removing them from the innermost groups they belong to). */
export function group(x: Ctx, axis: 'row' | 'col', on: boolean): void {
  const R = x.grid.range, key = axis === 'row' ? 'rowGroups' : 'colGroups', [a, b] = axis === 'row' ? [R.r1, R.r2] : [R.c1, R.c2];
  let list = [...((props(x)[key] ?? []) as Span[])];
  if (on) {
    if (list.some(g => g.start === a && g.end === b)) return;
    if (list.filter(g => g.start <= a && g.end >= b).length >= 7) { toast('그룹은 8단계까지만 중첩할 수 있습니다'); return; }
    list.push({ start: a, end: b });
  } else {
    const hits = list.filter(g => g.start <= b && g.end >= a);
    if (!hits.length) return;
    // the innermost overlapping groups lose the selected span (a group may split in two)
    const inner = hits.filter(g => !hits.some(o => o !== g && g.start <= o.start && g.end >= o.end));
    list = list.flatMap(g => {
      if (!inner.includes(g)) return [g];
      const parts: Span[] = [];
      if (g.start < a) parts.push({ ...g, end: a - 1 });
      if (g.end > b) parts.push({ ...g, start: b + 1 });
      return parts;
    });
  }
  setProps(x, { [key]: list.length ? list : null });
}
export function setGroupsCollapsed(x: Ctx, axis: 'row' | 'col', collapsed: boolean): void {
  const key = axis === 'row' ? 'rowGroups' : 'colGroups', list = (props(x)[key] ?? []) as Span[];
  if (list.length) setProps(x, { [key]: list.map(g => ({ ...g, collapsed })) });
}

/**
 * Formulas anywhere in the document rewritten by `fn` (given the formula and the name of its sheet), except the cells
 * `skip` says are being rewritten already.
 */
function rewriteFormulas(x: Ctx, fn: (raw: string, sheetName: string) => string, skip: (sheet: number, r: number, c: number) => boolean): Op[] {
  const ops: Op[] = [], all = x.model.wb.toJSON();
  for (const meta of x.model.sheets) for (const cell of all[meta.name] ?? []) {
    if (typeof cell.raw !== 'string' || !cell.raw.startsWith('=') || skip(meta.id, cell.r, cell.c)) continue;
    const next = fn(cell.raw, meta.name);
    if (next !== cell.raw) ops.push({ t: 'set', sheet: meta.id, r: cell.r, c: cell.c, raw: next });
  }
  return ops;
}

/** Edit > Move: swap the selected rows (or columns) with the neighbour block; references follow their cells, as in Sheets. */
export function moveBlock(x: Ctx, axis: 'row' | 'col', dir: -1 | 1): void {
  const R = x.grid.range, s = x.sheet(), m = x.model, used = m.used(s), name = m.sheetName(s)!;
  const [a, b] = axis === 'row' ? [R.r1, R.r2] : [R.c1, R.c2];
  if ((dir < 0 && a === 0)) return;
  const other = dir < 0 ? a - 1 : b + 1;
  const span = axis === 'row' ? used.cols : used.rows;
  // where each row/column ends up: the block moves by one, the neighbour goes to the other side
  const to = (i: number): number => (i >= a && i <= b ? i + dir : i === other ? (dir < 0 ? b : a) : i);
  const remap = (raw: string, onSheet: string): string => (raw.startsWith('=') ? moveFormula(raw, onSheet, name, axis, to) : raw);
  const ops: Op[] = [];
  const copyLine = (from: number, toLine: number): void => {
    for (let k = 0; k < span; k++) {
      const [fr, fc, tr, tc] = axis === 'row' ? [from, k, toLine, k] : [k, from, k, toLine];
      const raw = m.raw(s, fr, fc), fmt = m.fmt(s, fr, fc);
      ops.push({ t: 'set', sheet: s, r: tr, c: tc, raw: raw ? remap(raw, name) : null });
      ops.push({ t: 'fmt', sheet: s, r1: tr, c1: tc, r2: tr, c2: tc, fmt: Object.fromEntries(ALL_FMT_KEYS.map(key => [key, fmt && key in fmt ? fmt[key] : null])) });
    }
  };
  const lines = Array.from({ length: b - a + 1 }, (_, i) => a + i);
  const snapshot = (i: number): { raws: string[]; fmts: (Format | undefined)[] } => ({ raws: Array.from({ length: span }, (_, k) => (axis === 'row' ? m.raw(s, i, k) : m.raw(s, k, i))), fmts: Array.from({ length: span }, (_, k) => (axis === 'row' ? m.fmt(s, i, k) : m.fmt(s, k, i))) });
  const saved = snapshot(other);
  if (dir < 0) { for (const i of lines) copyLine(i, i - 1); }
  else { for (const i of [...lines].reverse()) copyLine(i, i + 1); }
  const dest = dir < 0 ? b : a;
  for (let k = 0; k < span; k++) {
    const [tr, tc] = axis === 'row' ? [dest, k] : [k, dest];
    ops.push({ t: 'set', sheet: s, r: tr, c: tc, raw: saved.raws[k] ? remap(saved.raws[k], name) : null });
    ops.push({ t: 'fmt', sheet: s, r1: tr, c1: tc, r2: tr, c2: tc, fmt: Object.fromEntries(ALL_FMT_KEYS.map(key => [key, saved.fmts[k] && key in saved.fmts[k]! ? saved.fmts[k]![key] : null])) });
  }
  // formulas elsewhere that point into the moved rows/columns
  ops.push(...rewriteFormulas(x, remap, (sh, r, c) => sh === s && ((axis === 'row' ? r : c) >= Math.min(a, other) && (axis === 'row' ? r : c) <= Math.max(b, other))));
  x.submit({ t: 'batch', ops });
  const d = dir;
  x.grid.selectRange(axis === 'row' ? { ...R, r1: R.r1 + d, r2: R.r2 + d } : { ...R, c1: R.c1 + d, c2: R.c2 + d });
}

/**
 * Insert cells, pushing the rest of the rows right (or the columns down); delete cells, pulling them back. As in Sheets,
 * references to the moved cells follow them (to deleted cells they become #REF!), and inserted cells keep the
 * formatting that was there.
 */
export function shiftCells(x: Ctx, dir: 'right' | 'down' | 'left' | 'up'): void {
  const R = x.grid.range, s = x.sheet(), m = x.model, used = m.used(s), ops: Op[] = [], name = m.sheetName(s)!;
  const horizontal = dir === 'right' || dir === 'left', grow = dir === 'right' || dir === 'down';
  const n = horizontal ? R.c2 - R.c1 + 1 : R.r2 - R.r1 + 1;
  const change: StructuralChange = { sheet: name, axis: horizontal ? 'col' : 'row', at: horizontal ? R.c1 : R.r1, count: grow ? n : -n, band: horizontal ? { lo: R.r1, hi: R.r2 } : { lo: R.c1, hi: R.c2 } };
  const remap = (raw: string, onSheet: string): string => (raw.startsWith('=') ? shiftFormula(raw, onSheet, change) : raw);
  const put = (r: number, c: number, fromR: number, fromC: number): void => {
    if (fromR < 0 || fromC < 0) { ops.push({ t: 'set', sheet: s, r, c, raw: null }); if (!grow) ops.push({ t: 'fmt', sheet: s, r1: r, c1: c, r2: r, c2: c, fmt: Object.fromEntries(ALL_FMT_KEYS.map(k => [k, null])) }); return; }
    const raw = m.raw(s, fromR, fromC), fmt = m.fmt(s, fromR, fromC);
    ops.push({ t: 'set', sheet: s, r, c, raw: raw ? remap(raw, name) : null });
    ops.push({ t: 'fmt', sheet: s, r1: r, c1: c, r2: r, c2: c, fmt: Object.fromEntries(ALL_FMT_KEYS.map(k => [k, fmt && k in fmt ? fmt[k] : null])) });
  };
  if (dir === 'right') for (let r = R.r1; r <= R.r2; r++) { for (let c = used.cols - 1; c >= R.c1; c--) put(r, c + n, r, c); for (let c = R.c1; c <= R.c2; c++) put(r, c, -1, -1); }
  if (dir === 'down') for (let c = R.c1; c <= R.c2; c++) { for (let r = used.rows - 1; r >= R.r1; r--) put(r + n, c, r, c); for (let r = R.r1; r <= R.r2; r++) put(r, c, -1, -1); }
  if (dir === 'left') for (let r = R.r1; r <= R.r2; r++) { for (let c = R.c1; c < used.cols; c++) put(r, c, r, c + n < used.cols ? c + n : -1); }
  if (dir === 'up') for (let c = R.c1; c <= R.c2; c++) { for (let r = R.r1; r < used.rows; r++) put(r, c, r + n < used.rows ? r + n : -1, c); }
  // formulas outside the moved cells that point into them
  const moved = (sh: number, r: number, c: number): boolean => sh === s && (horizontal ? r >= R.r1 && r <= R.r2 && c >= R.c1 : c >= R.c1 && c <= R.c2 && r >= R.r1);
  ops.push(...rewriteFormulas(x, remap, moved));
  if (ops.length) x.submit({ t: 'batch', ops });
}

// ----- data clean-up -----
/**
 * Data > Data cleanup > Remove duplicates: rows whose values in `cols` (all columns by default) repeat an earlier row
 * are removed and the rest move up inside the range; a header row is left alone.
 */
export function removeDuplicates(x: Ctx, opt: { header?: boolean; cols?: number[] } = {}): { removed: number; remaining: number } {
  const R = x.grid.range, s = x.sheet(), m = x.model, seen = new Set<string>(), keep: string[][] = [];
  const first = R.r1 + (opt.header ? 1 : 0), cols = opt.cols?.length ? opt.cols : Array.from({ length: R.c2 - R.c1 + 1 }, (_, j) => R.c1 + j);
  for (let r = first; r <= R.r2; r++) {
    const row = Array.from({ length: R.c2 - R.c1 + 1 }, (_, j) => m.raw(s, r, R.c1 + j));
    const k = cols.map(c => m.display(s, r, c).text.trim().toLowerCase()).join('\u0001');
    if (!seen.has(k)) { seen.add(k); keep.push(row); }
  }
  const removed = R.r2 - first + 1 - keep.length, ops: Op[] = [];
  for (let r = first; r <= R.r2; r++) for (let c = R.c1; c <= R.c2; c++) {
    const v = keep[r - first]?.[c - R.c1] ?? '';
    if (v !== m.raw(s, r, c)) ops.push({ t: 'set', sheet: s, r, c, raw: v || null });
  }
  if (ops.length) x.submit({ t: 'batch', ops });
  return { removed, remaining: keep.length };
}
export function trimWhitespace(x: Ctx): number {
  const R = x.grid.range, s = x.sheet(), m = x.model, ops: Op[] = [];
  for (let r = R.r1; r <= R.r2; r++) for (let c = R.c1; c <= R.c2; c++) {
    const v = m.raw(s, r, c); if (!v || v.startsWith('=')) continue;
    const t = v.replace(/\s+/g, ' ').trim();
    if (t !== v) ops.push({ t: 'set', sheet: s, r, c, raw: t || null });
  }
  if (ops.length) x.submit({ t: 'batch', ops });
  return ops.length;
}
/**
 * Data > Split text to columns: each piece is read as if typed ("2026-01-05" a date, "10%" a percentage). `texts` are
 * the column's original contents (to split again with another separator); `clear` columns from an earlier split are emptied.
 * Returns how many columns the widest row took.
 */
export function splitText(x: Ctx, sep: string, R: Range = x.grid.range, texts?: string[], clear = 0): number {
  const s = x.sheet(), m = x.model, ops: Op[] = [];
  let widest = 1;
  for (let r = R.r1; r <= R.r2; r++) {
    const v = texts ? texts[r - R.r1] : m.raw(s, r, R.c1);
    if (!v || v.startsWith('=')) continue;
    const parts = sep === 'auto' ? v.split(/[,;\t]/) : sep === ' ' ? v.split(/\s+/) : v.split(sep);
    widest = Math.max(widest, parts.length);
    for (let j = 0; j < Math.max(parts.length, clear); j++) ops.push(...m.inputOps(s, r, R.c1 + j, (parts[j] ?? '').trim()));
  }
  if (ops.length) x.submit({ t: 'batch', ops });
  return widest;
}
export function shuffle(x: Ctx): void {
  const R = x.grid.range, s = x.sheet(), m = x.model;
  const rows = Array.from({ length: R.r2 - R.r1 + 1 }, (_, i) => Array.from({ length: R.c2 - R.c1 + 1 }, (_, j) => m.raw(s, R.r1 + i, R.c1 + j)));
  for (let i = rows.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [rows[i], rows[j]] = [rows[j], rows[i]]; }
  const ops: Op[] = [];
  rows.forEach((row, i) => row.forEach((v, j) => { if (v !== m.raw(s, R.r1 + i, R.c1 + j)) ops.push({ t: 'set', sheet: s, r: R.r1 + i, c: R.c1 + j, raw: v || null }); }));
  if (ops.length) x.submit({ t: 'batch', ops });
}

/** Filter menu > 색상을 기준으로 정렬: rows whose cell in `col` has that fill (or text) colour come first, the rest keep their order. */
export function sortByColor(x: Ctx, R: Range, col: number, kind: 'bg' | 'color', color: string): void {
  const s = x.sheet(), m = x.model, want = color.toLowerCase();
  const has = (r: number): boolean => String(m.fmt(s, r, col)?.[kind] ?? '').toLowerCase() === want;
  const order = Array.from({ length: R.r2 - R.r1 + 1 }, (_, i) => R.r1 + i).sort((a, b) => Number(has(b)) - Number(has(a)) || a - b);
  reorderRows(x, R, order);
}
/** Put the rows of `R` in the given order (row numbers before the change), formats and formulas moving with them. */
function reorderRows(x: Ctx, R: Range, order: number[]): void {
  const s = x.sheet(), m = x.model, ops: Op[] = [];
  order.forEach((from, i) => {
    const r = R.r1 + i;
    for (let c = R.c1; c <= R.c2; c++) {
      const raw = m.raw(s, from, c), moved = raw.startsWith('=') ? translateFormula(raw, r - from, 0) : raw;
      if (moved !== m.raw(s, r, c)) ops.push({ t: 'set', sheet: s, r, c, raw: moved || null });
      const f = m.fmt(s, from, c), cur = m.fmt(s, r, c);
      if (JSON.stringify(f ?? {}) !== JSON.stringify(cur ?? {})) ops.push({ t: 'fmt', sheet: s, r1: r, c1: c, r2: r, c2: c, fmt: Object.fromEntries(ALL_FMT_KEYS.map(k => [k, f && k in f ? f[k] : null])) });
    }
  });
  if (ops.length) x.submit({ t: 'batch', ops });
}

/** Sort the rows of `R` by one or more columns; formulas move with their rows. */
export function sortRange(x: Ctx, R: Range, keys: { col: number; asc: boolean }[], header = false): void {
  const s = x.sheet(), m = x.model, start = header ? R.r1 + 1 : R.r1;
  if (start >= R.r2 && R.r2 === start) return;
  const rows: { from: number; raws: string[]; fmts: (Format | undefined)[]; keys: unknown[] }[] = [];
  for (let r = start; r <= R.r2; r++) rows.push({ from: r, raws: Array.from({ length: R.c2 - R.c1 + 1 }, (_, j) => m.raw(s, r, R.c1 + j)), fmts: Array.from({ length: R.c2 - R.c1 + 1 }, (_, j) => m.fmt(s, r, R.c1 + j)), keys: keys.map(k => m.value(s, r, k.col)) });
  // Sheets: numbers, then text, then TRUE/FALSE going up; the whole order reversed going down - blanks always last
  const rank = (v: unknown): number => (v === null || v === '' ? 3 : typeof v === 'number' ? 0 : typeof v === 'string' ? 1 : 2);
  rows.sort((a, b) => {
    for (let i = 0; i < keys.length; i++) {
      const x1 = a.keys[i], y1 = b.keys[i], ra = rank(x1), rb = rank(y1);
      if (ra !== rb) return ra === 3 || rb === 3 || keys[i].asc ? ra - rb : rb - ra;
      if (ra === 3) continue;
      const c = typeof x1 === 'string' ? x1.localeCompare(y1 as string, 'ko', { sensitivity: 'base' }) : (x1 as number) < (y1 as number) ? -1 : (x1 as number) > (y1 as number) ? 1 : 0;
      if (c) return keys[i].asc ? c : -c;
    }
    return a.from - b.from;
  });
  const ops: Op[] = [];
  rows.forEach((row, i) => row.raws.forEach((raw, j) => {
    const r = start + i, c = R.c1 + j, moved = raw.startsWith('=') ? translateFormula(raw, r - row.from, 0) : raw;
    if (moved !== m.raw(s, r, c)) ops.push({ t: 'set', sheet: s, r, c, raw: moved || null });
    const f = row.fmts[j], cur = m.fmt(s, r, c);
    if (JSON.stringify(f ?? {}) !== JSON.stringify(cur ?? {})) ops.push({ t: 'fmt', sheet: s, r1: r, c1: c, r2: r, c2: c, fmt: Object.fromEntries(ALL_FMT_KEYS.map(k => [k, f && k in f ? f[k] : null])) });
  }));
  if (ops.length) x.submit({ t: 'batch', ops });
}

// ----- exports -----
export function sheetText(x: Ctx, sep: ',' | '\t'): string {
  const s = x.sheet(), used = x.model.used(s), lines: string[] = [];
  for (let r = 0; r < used.rows; r++) {
    const cells: string[] = [];
    for (let c = 0; c < used.cols; c++) { const t = x.model.display(s, r, c).text; cells.push(new RegExp(`["\n${sep === '\t' ? '\t' : ','}]`).test(t) ? `"${t.replace(/"/g, '""')}"` : t); }
    lines.push(cells.join(sep));
  }
  return lines.join('\r\n');
}
export function sheetHtml(x: Ctx, title: string): string {
  const s = x.sheet(), used = x.model.used(s);
  const e = (v: string): string => v.replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
  let rows = '';
  for (let r = 0; r < used.rows; r++) {
    rows += '<tr>';
    for (let c = 0; c < used.cols; c++) {
      const d = x.model.display(s, r, c), f = d.fmt ?? {};
      const st = [f.b ? 'font-weight:bold' : '', f.i ? 'font-style:italic' : '', d.ink ?? f.color ? `color:${d.ink ?? f.color}` : '', f.bg ? `background:${f.bg}` : '', `text-align:${f.ha === 'distributed' ? 'justify' : f.ha ?? (d.kind === 'num' ? 'right' : 'left')}`].filter(Boolean).join(';');
      rows += `<td style="${e(st)}">${e(d.text)}</td>`;
    }
    rows += '</tr>\n';
  }
  return `<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>${e(title)}</title><style>table{border-collapse:collapse;font:10pt Arial,sans-serif}td{border:1px solid #ccc;padding:2px 4px}</style></head><body><table>\n${rows}</table></body></html>`;
}
export function download(name: string, content: string, type: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([content], { type }));
  a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
